/**
 * Per-day statistics (first / last / min / max / average) for a timestamped
 * series such as hourly temperature or ETc, bucketed by calendar day in the
 * field's timezone.
 *
 * Performance: dayjs's `.tz()` formats the instant through
 * `Date#toLocaleString({ timeZone })` on every call, ~35µs each. A year of
 * hourly temperature is ~14k samples, so calling it per sample cost ~500ms of
 * main thread per call — and the web Farm View field-details graph runs this
 * once per WP sensor response, which blocked clicks on the next field.
 *
 * Samples arrive sorted, so the timezone is resolved once per day instead:
 * after a day starts, any sample in [dayStart, dayStart + MIN_DAY_SECONDS) is
 * the same calendar day and skips `.tz()`. Past that window (or if a sample is
 * out of order) it falls back to resolving that sample, so the output is
 * identical to resolving every sample.
 *
 * CommonJS and platform-agnostic so it runs on web, mobile and `node --test`.
 */

const daysjs = require('./dayjsConfig');

// Shortest calendar day any timezone can have. DST makes a 23h day and the
// largest shift in the tz database (Antarctica/Troll, 2h) makes a 22h day.
// Correctness relies on this: a shorter day would put its successor's first
// samples in the wrong bucket.
const MIN_DAY_SECONDS = 22 * 60 * 60;

const weatherDataStatistics = (weatherData, timezone) => {
  if (!weatherData) {
    return {};
  }

  const keys = Object.keys(weatherData);
  keys.sort();

  const informationDic = {};
  let currentCounter = 0;
  let currentDateKey = undefined;
  let currentSum = 0;
  let currentDayStartUnix = undefined;
  let sameDayUntilUnix = -Infinity;

  // date string (YYYY-MM-DD) -> unix timestamp of that day's start
  const dateKeyToUnix = {};

  for (let i = 0; i < keys.length; i++) {
    const currentValue = weatherData[keys[i]];
    const timestamp = parseInt(keys[i]);

    const knownSameDay =
      timestamp >= currentDayStartUnix && timestamp < sameDayUntilUnix;

    let startsNewDay = false;
    let tmpDateObject;
    let dateKey;
    if (!knownSameDay) {
      tmpDateObject = timezone
        ? daysjs.unix(timestamp).tz(timezone)
        : daysjs.unix(timestamp);
      dateKey = tmpDateObject.format('YYYY-MM-DD');
      startsNewDay = dateKey !== currentDateKey;
    }

    if (startsNewDay) {
      // Completing the average calculation for previous day
      if (
        currentDateKey &&
        !informationDic[dateKeyToUnix[currentDateKey]]['averageValue']
      ) {
        informationDic[dateKeyToUnix[currentDateKey]]['averageValue'] = (
          currentSum / currentCounter
        ).toFixed(2);
      }

      currentDateKey = dateKey;

      const dayStartUnix = tmpDateObject.startOf('day').unix();
      dateKeyToUnix[currentDateKey] = dayStartUnix;
      currentDayStartUnix = dayStartUnix;
      sameDayUntilUnix = dayStartUnix + MIN_DAY_SECONDS;

      informationDic[dayStartUnix] = {
        firstValue: currentValue,
        lastValue: currentValue,
        minValue: currentValue,
        maxValue: currentValue,
        averageValue: undefined,
      };
      currentCounter = 1;
      currentSum = currentValue;
    } else {
      const day = informationDic[dateKeyToUnix[currentDateKey]];
      if (currentValue < day['minValue']) {
        day['minValue'] = currentValue;
      }
      if (currentValue > day['maxValue']) {
        day['maxValue'] = currentValue;
      }
      day['lastValue'] = currentValue;
      currentSum += currentValue;
      currentCounter += 1;
    }
  }

  // Completing the average calculation
  if (
    currentDateKey &&
    !informationDic[dateKeyToUnix[currentDateKey]]['averageValue']
  ) {
    informationDic[dateKeyToUnix[currentDateKey]]['averageValue'] = (
      currentSum / currentCounter
    ).toFixed(2);
  }

  return informationDic;
};

module.exports = { weatherDataStatistics, MIN_DAY_SECONDS };
